import { cn } from "@client/utils/cn";
import { roundMemberRoleLabel } from "@lib/roundMemberRole";
import { roundMemberRole, type RoundMemberRole } from "@engine/round/actions";
import {
  Button,
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuTrigger,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@sfc/ui";
import { ChevronDownIcon } from "lucide-react";
import {
  type FieldValues,
  type Path,
  type UseFormReturn,
} from "react-hook-form";

export const descriptionByRole: Record<RoundMemberRole, string> = {
  admin: "Can manage round, proposals, and users",
  funder: "Can contribute funds and vote on proposals",
  recommender: "Can review and recommend proposals",
};

interface FormValues extends FieldValues {
  roles: RoundMemberRole[];
}

export function FormRoleField<TFormValues extends FormValues>({
  form,
  requiredRoles,
}: {
  form: UseFormReturn<TFormValues>;
  requiredRoles?: Partial<Record<RoundMemberRole, string>>;
}) {
  const availableRoles = roundMemberRole.options as readonly RoundMemberRole[];

  return (
    <FormField
      control={form.control}
      name={"roles" as Path<TFormValues>}
      render={({ field }) => {
        const selectedRoles = (field.value ?? []) as RoundMemberRole[];

        return (
          <FormItem className="flex-1">
            <FormLabel>Role</FormLabel>
            <FormControl>
              <DropdownMenu>
                <DropdownMenuTrigger
                  render={
                    <Button
                      className={cn(
                        form.formState.errors.roles &&
                          "ring-destructive/20 dark:ring-destructive/40 border-destructive",
                      )}
                      variant="outline"
                    />
                  }
                >
                  <span className="flex-1 text-start">
                    {selectedRoles.length > 0
                      ? selectedRoles
                          .map(
                            (role: RoundMemberRole) =>
                              roundMemberRoleLabel[role],
                          )
                          .join(", ")
                      : "Select roles"}
                  </span>
                  <ChevronDownIcon className="size-4" />
                </DropdownMenuTrigger>
                <DropdownMenuContent className="w-[var(--anchor-width)]">
                  {availableRoles.map((role: RoundMemberRole) => {
                    const disabled = Boolean(requiredRoles?.[role]);

                    return (
                      <Tooltip key={role} open={disabled ? undefined : false}>
                        <TooltipTrigger render={<div />}>
                          <DropdownMenuCheckboxItem
                            checked={selectedRoles.includes(role)}
                            onCheckedChange={(value) =>
                              field.onChange(
                                value
                                  ? [...selectedRoles, role]
                                  : selectedRoles.filter(
                                      (selectedRole: RoundMemberRole) =>
                                        selectedRole !== role,
                                    ),
                              )
                            }
                            disabled={disabled}
                          >
                            <span className="flex flex-col">
                              {roundMemberRoleLabel[role]}
                              <span className="text-xs text-muted-foreground">
                                {descriptionByRole[role]}
                              </span>
                            </span>
                          </DropdownMenuCheckboxItem>
                        </TooltipTrigger>
                        <TooltipContent side="left">
                          {requiredRoles?.[role]}
                        </TooltipContent>
                      </Tooltip>
                    );
                  })}
                </DropdownMenuContent>
              </DropdownMenu>
            </FormControl>
            <FormMessage />
          </FormItem>
        );
      }}
    />
  );
}
